import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder } from '@angular/forms'
import { Arn, SelectableResource } from 'src/utils/types'
import {
  getResourceTypeDetails,
  ResourceType,
  ResourceTypeClassification,
  ResourceTypeDetails,
  resourceTypeFromValue,
  ResourceTypeInfo,
  ResourceVariant,
} from 'src/utils/resource-types'
import { diffSets, equalSets } from '../utils'
import { List } from 'immutable'
import { K8sClusterResourcesTreeNode, ResourceNodeContext, ResourceTreeNode } from '../resource-tree-node'

export interface UserSelectableResource extends SelectableResource {
  displayName: string
  displayNameAsChild: string
  shouldRender: boolean
  shouldRenderAsChild: boolean
  resourceTypeDetails: ResourceTypeDetails
}

export interface ResourceSelections {
  selectedResources: Array<Arn>
  suppressedResources: Array<Arn>
}

export function resourceSelectionsAsSets(rs: ResourceSelections) {
  return {
    selectedResources: new Set<Arn>(rs.selectedResources),
    suppressedResources: new Set<Arn>(rs.suppressedResources),
  }
}

// A typespec for a function that takes a ResourceSelections object as an arg and returns void
interface SelectionChangeFunc {
  (resourceSelections: ResourceSelections): void
}

function makeResourceTypeInfoKey(info: ResourceTypeInfo) {
  let str = info.resourceType + '|'
  if (info.resourceVariant !== null && info.resourceVariant !== undefined) {
    str += info.resourceVariant
  }
  return str
}

/**
 * A resource renders as a top-level group of its type, or beneath its parent unless its type is
 * suppressed as a child (e.g. PostgreSQL configurations, which are always copied with the server).
 */
function isRenderable(res: UserSelectableResource) {
  return res.shouldRender || (!!res.parentArn && res.shouldRenderAsChild)
}

function flattenNodes(rootNodes: ResourceTreeNode[], level = 0): ResourceTreeNode[] {
  const flattedNodes: ResourceTreeNode[] = []
  for (let node of rootNodes) {
    node.level = level
    flattedNodes.push(node)
    if (node.expanded && node.children.length > 0) {
      const children = node.children.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
      flattedNodes.push(...flattenNodes(children, level + 1))
    }
  }
  return flattedNodes
}

function parseResourceTypeInfoKey(str: string): ResourceTypeInfo | null {
  const parts = str.split('|', 2)
  const type = resourceTypeFromValue(parts[0])

  if (!type) {
    console.warn('Invalid resource type', str)
    return null
  }
  let variant: ResourceVariant = null
  if (parts.length > 1 && parts[1]) {
    variant = parts[1] as ResourceVariant
  }
  return {
    resourceType: type,
    resourceVariant: variant,
  }
}

interface ResourceTypeData {
  resType: ResourceType
  resVariant: ResourceVariant
  resourceTypeDetails: ResourceTypeDetails
  rootNodes: ResourceTreeNode[]
  nodes: ResourceTreeNode[]
}

@Component({
  selector: 'app-resources-selector',
  templateUrl: './app-resources-selector.component.html',
  styleUrls: ['./app-resources-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppResourcesSelectorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppResourcesSelectorComponent implements OnDestroy, OnChanges, ControlValueAccessor, ResourceNodeContext {
  @Input() sourceResources: List<UserSelectableResource> = null
  @Input() refreshing = false
  @Input() filterString = ''
  @Input() rpo: number = null
  @Input() readonly: boolean = false
  @Output() viewIssues = new EventEmitter<void>()

  public selectedResources = new Array<Arn>()
  public suppressedResources = new Array<Arn>()
  private selectionChangeFunc: SelectionChangeFunc = null
  private selectedTab: ResourceTypeClassification = ResourceTypeClassification.network
  private _sortedResourcesByType: ResourceTypeData[]
  private _filteredResourcesByArn?: Map<string, UserSelectableResource> = null
  private _sourceResourcesByArn?: Map<string, UserSelectableResource> = null
  private _hiddenDescendantCounts?: Map<string, number> = null
  private _expandedArns = new Set<Arn>()

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnDestroy(): void {}

  private get filteredSourceResources() {
    const filterString = this.filterString.trim().toLocaleLowerCase()
    function resourceMatches(res) {
      return (
        isRenderable(res) &&
        (filterString === '' ||
          res.resourceId.toLowerCase().search(filterString) >= 0 ||
          res.resourceName.toLowerCase().search(filterString) >= 0 ||
          res.displayResourceType.toLowerCase().search(filterString) >= 0)
      )
    }

    const matchingResources = this.sourceResources.filter(resourceMatches)
    const matchingArns = new Set<string>(matchingResources.map((res) => res.resourceArn))

    // Add all ancestors of matching resources to the filtered list
    const sourceResourcesByArn = this.sourceResourcesByArn
    const ancestors = []
    for (let res of matchingResources) {
      while (res && res.parentArn && !matchingArns.has(res.parentArn)) {
        let parent = sourceResourcesByArn.get(res.parentArn)
        if (parent) {
          ancestors.push(parent)
          matchingArns.add(parent.resourceArn)
        }
        res = parent
      }
    }

    return [...matchingResources, ...ancestors]
  }

  get sourceResourcesByArn() {
    if (!this._sourceResourcesByArn) {
      const resByArn = new Map<string, UserSelectableResource>()
      this.sourceResources.forEach((res) => {
        resByArn.set(res.resourceArn, res)
      })
      this._sourceResourcesByArn = resByArn
    }

    return this._sourceResourcesByArn
  }

  /**
   * Backend descendant counts include the child types we suppress, so tally the hidden ones under
   * each ancestor.  Without this a parent's ratio counts rows that aren't there, and a parent whose
   * children are all suppressed keeps an expand caret that opens onto nothing.
   */
  private get hiddenDescendantCounts() {
    if (this._hiddenDescendantCounts) {
      return this._hiddenDescendantCounts
    }

    const resByArn = this.sourceResourcesByArn
    const hiddenByArn = new Map<string, boolean>()

    // A resource is hidden when its own type is suppressed as a child, or when it sits under one.
    const isHidden = (res: UserSelectableResource): boolean => {
      const cached = hiddenByArn.get(res.resourceArn)
      if (cached !== undefined) {
        return cached
      }
      let hidden = false
      if (res.parentArn) {
        const parent = resByArn.get(res.parentArn)
        hidden = !res.shouldRenderAsChild || (!!parent && isHidden(parent))
      }
      hiddenByArn.set(res.resourceArn, hidden)
      return hidden
    }

    const counts = new Map<string, number>()
    this.sourceResources.forEach((res) => {
      if (!isHidden(res)) {
        return
      }
      let ancestorArn = res.parentArn
      while (ancestorArn) {
        counts.set(ancestorArn, (counts.get(ancestorArn) || 0) + 1)
        ancestorArn = resByArn.get(ancestorArn)?.parentArn
      }
    })

    this._hiddenDescendantCounts = counts
    return this._hiddenDescendantCounts
  }

  get sortedResourcesByType() {
    /**
     * Produces an array of objects containing resources of each resource
     * type and variant.  The array is sorted according to a sort priority
     * specified above.
     */
    if (this._sortedResourcesByType) {
      return this._sortedResourcesByType
    }

    const resByArn = new Map<string, UserSelectableResource>()
    const nodes: ResourceTreeNode[] = []
    const nodesByArn = new Map<string, ResourceTreeNode>()
    const nodesByType = new Map<string, ResourceTreeNode[]>()
    const hiddenDescendantCounts = this.hiddenDescendantCounts

    this.filteredSourceResources.forEach((res) => {
      const typeInfo = { resourceType: res.resourceType, resourceVariant: res.resourceVariant }
      let typeClassification = res.resourceTypeDetails.classification
      // Compute a string key for our complex type so type and variant are
      // considered when we use the map.
      const typeKey = makeResourceTypeInfoKey(typeInfo)
      if (isRenderable(res) && res.resourceType && typeClassification === this.selectedTab) {
        const node = new ResourceTreeNode(res, this, hiddenDescendantCounts.get(res.resourceArn) || 0)
        // Retain the previous expanded state of the node
        node.expanded = this._expandedArns.has(node.resource.resourceArn)

        nodes.push(node)
        nodesByArn.set(res.resourceArn, node)
        resByArn.set(res.resourceArn, res)

        if (!nodesByType.has(typeKey)) {
          nodesByType.set(typeKey, [])
        }
        nodesByType.get(typeKey).push(node)
      }
    })

    const clusterResourceNodes = new Map<string, K8sClusterResourcesTreeNode>()

    // Add parent/child node pointers
    for (let node of nodes) {
      if (node.resource.parentArn) {
        let parentNode = nodesByArn.get(node.resource.parentArn)

        // Inject a "Cluster Resources" node to hold non-namespaced k8s resources
        if (
          parentNode &&
          (parentNode.resource.resourceType == ResourceType.eksCluster ||
            parentNode.resource.resourceType == ResourceType.azureAksCluster) &&
          node.resource.resourceVariant != 'Namespace'
        ) {
          const clusterNode = parentNode
          let clusterResourcesNode = clusterResourceNodes.get(parentNode.resource.resourceArn)
          if (!clusterResourcesNode) {
            clusterResourcesNode = new K8sClusterResourcesTreeNode(parentNode.resource, this)
            clusterResourcesNode.parent = clusterNode
            clusterNode.children.push(clusterResourcesNode)
            clusterResourceNodes.set(parentNode.resource.resourceArn, clusterResourcesNode)
          }
          parentNode = clusterResourcesNode
        }

        if (parentNode) {
          parentNode.children.push(node)
          node.parent = parentNode
        }
      }
    }

    let resTypes = Array.from(nodesByType.keys())
      .map((key) => parseResourceTypeInfoKey(key))
      .filter((info) => info !== null)
    const detailsCache = new Map<string, ResourceTypeDetails>()
    for (const info of resTypes) {
      detailsCache.set(makeResourceTypeInfoKey(info), getResourceTypeDetails(info))
    }
    let sortedResTypes = resTypes.sort((info1, info2) => {
      // sort by resource type priority first, then by type and variant enums
      const priority1 = detailsCache.get(makeResourceTypeInfoKey(info1)).sortOrder
      const priority2 = detailsCache.get(makeResourceTypeInfoKey(info2)).sortOrder
      if (priority1 < priority2) {
        return -1
      } else if (priority1 > priority2) {
        return 1
      } else if (info1.resourceType < info2.resourceType) {
        return -1
      } else if (info1.resourceType > info2.resourceType) {
        return 1
      } else if (info1.resourceVariant === null && info2.resourceVariant !== null) {
        return -1
      } else if (info1.resourceVariant !== null && info2.resourceVariant === null) {
        return 1
      } else if (info1.resourceVariant < info2.resourceVariant) {
        return -1
      } else if (info1.resourceVariant > info2.resourceVariant) {
        return 1
      }
      return 0
    })

    const preparedResourceArray = new Array<ResourceTypeData>()

    for (let info of sortedResTypes) {
      // Parse the key string back into a structure so we can include
      // the individual attributes in the output objects.
      const typeKey = makeResourceTypeInfoKey(info)
      const nodes = nodesByType.get(typeKey).sort((n1, n2) => {
        return n1.sortKey.toLowerCase() < n2.sortKey.toLowerCase() ? -1 : 1
      })
      const rootNodes = nodes.filter((node) => !node.resource.parentArn)

      if (rootNodes.length > 0) {
        preparedResourceArray.push({
          resType: info.resourceType,
          resVariant: info.resourceVariant,
          resourceTypeDetails: detailsCache.get(typeKey),
          rootNodes: rootNodes,
          nodes: flattenNodes(rootNodes),
        })
      }
    }

    this._sortedResourcesByType = preparedResourceArray
    this._filteredResourcesByArn = resByArn
    return this._sortedResourcesByType
  }

  onNodeExpanded(node: ResourceTreeNode) {
    this._expandedArns.add(node.resource.resourceArn)
    this._reflattenNodeType(node)
  }

  onNodeCollapsed(node: ResourceTreeNode) {
    this._expandedArns.delete(node.resource.resourceArn)
    this._reflattenNodeType(node)
  }

  private _reflattenNodeType(node: ResourceTreeNode) {
    while (node.parent) {
      node = node.parent
    }
    const rootKey = node.key
    this._sortedResourcesByType = this._sortedResourcesByType.map((resTypeData) => {
      if (resTypeData.rootNodes.some((r) => r.key === rootKey)) {
        resTypeData.nodes = flattenNodes(resTypeData.rootNodes)
      }
      return resTypeData
    })
  }

  resourceTypeTrackBy(
    _index: number,
    resourceArrayEntry: { resType: ResourceType; resVariant: ResourceVariant; resources: Array<UserSelectableResource> }
  ) {
    return [resourceArrayEntry.resType, resourceArrayEntry.resVariant]
  }

  nodeTrackBy(_index: number, node: ResourceTreeNode) {
    return node.key
  }

  ngOnChanges(changes: SimpleChanges) {
    /**
     * When the sourceResources array changes (i.e. the user refreshed the source region),
     * we need to remove any selected resources that no longer exist.  This is harder than
     * you'd think.  We diff the before and after resource IDs and any that have been
     * removed then get filtered out of the selectedResources array.
     */
    let publishSelections = false

    if ('sourceResources' in changes && changes.sourceResources.previousValue === undefined) {
      // Initial load — auto-select the first tab that has resources (e.g. Azure apps with no network resources)
      this._sourceResourcesByArn = null
      this._hiddenDescendantCounts = null
      this.autoSelectFirstAvailableTab()
    } else if ('sourceResources' in changes) {
      // Invalidate cache of source resources by ARN
      this._sourceResourcesByArn = null
      this._hiddenDescendantCounts = null

      let afterArns = new Set(changes.sourceResources.currentValue.map((res) => res.resourceArn))
      let beforeArns = new Set(changes.sourceResources.previousValue.map((res) => res.resourceArn))
      let deletedArns = diffSets(beforeArns, afterArns)
      let deletedArnSet = new Set(deletedArns)
      let oldSelectedResources = new Set(this.selectedResources)
      let newSelectedResources = new Set(this.selectedResources.filter((resArn) => !deletedArnSet.has(resArn)))
      if (!equalSets(oldSelectedResources, newSelectedResources)) {
        this.selectedResources = Array.from(newSelectedResources)
        publishSelections = true
      }

      let oldSuppressedResources = new Set(this.suppressedResources)
      let newSuppressedResources = new Set(this.suppressedResources.filter((resArn) => !deletedArnSet.has(resArn)))
      if (!equalSets(oldSuppressedResources, newSuppressedResources)) {
        this.suppressedResources = Array.from(newSuppressedResources)
        publishSelections = true
      }

      if (!equalSets(beforeArns, afterArns)) {
        // Some source resources were added or deleted.  Let's fully recompute the resourcesByType array.
        this._sortedResourcesByType = null
        // The current tab may no longer have content after resources changed
        this.autoSelectFirstAvailableTab()
      } else if (this._filteredResourcesByArn) {
        // Existing resources were changed (due to checkbox or tag selections).  Let's patch in the changes.
        changes.sourceResources.currentValue.forEach((res: UserSelectableResource) => {
          const existingRes = this._filteredResourcesByArn.get(res.resourceArn)
          if (existingRes) {
            if (existingRes.arnMatch != res.arnMatch) {
              existingRes.arnMatch = res.arnMatch
            }
            if (existingRes.dependeeOf != res.dependeeOf) {
              existingRes.dependeeOf = res.dependeeOf
            }
            if (existingRes.weakDependeeOf != res.weakDependeeOf) {
              existingRes.weakDependeeOf = res.weakDependeeOf
            }
            if (existingRes.directDependeeOf != res.directDependeeOf) {
              existingRes.directDependeeOf = res.directDependeeOf
            }
            if (existingRes.matchingRules != res.matchingRules) {
              existingRes.matchingRules = res.matchingRules
            }
            if (existingRes.suppressedWeakDependeeOf != res.suppressedWeakDependeeOf) {
              existingRes.suppressedWeakDependeeOf = res.suppressedWeakDependeeOf
            }
            if (existingRes.isExcluded != res.isExcluded) {
              existingRes.isExcluded = res.isExcluded
            }
          }
        })
      }

      if (publishSelections) {
        // We only want to call this if the selection has changed as it triggers an expensive re-calculation
        // of the resources passed into this component.
        this.publishSelections()
      }
    } else if ('filterString' in changes) {
      this._sortedResourcesByType = null
    }
  }

  private publishSelections() {
    this.selectionChangeFunc({
      selectedResources: this.selectedResources,
      suppressedResources: this.suppressedResources,
    })
  }

  writeValue(resourceSelections: ResourceSelections): void {
    /**
     * Accept a value passed into this control from the embedding form.
     */
    if (resourceSelections !== undefined) {
      this.selectedResources = resourceSelections.selectedResources
      this.suppressedResources = resourceSelections.suppressedResources
    }
  }

  registerOnChange(fn: SelectionChangeFunc): void {
    /**
     * Register a function to be called any time this control's value (which is a list of
     * resource IDs) changes.
     */
    this.selectionChangeFunc = fn
  }

  registerOnTouched(fn: any): void {
    // nothing to do here - we don't handle touch events
  }

  onNodeSelectionChanged(node: ResourceTreeNode) {
    /**
     * Handle a checkbox click by updating the list of selected resources.
     */
    const resource = node.resource
    let resourceArn = resource.resourceArn

    if (this.selectedResources.some((arn) => resourceArn === arn)) {
      // The resource is directly selected.  Filter it out
      this.selectedResources = this.selectedResources.filter((entry: Arn) => entry !== resourceArn)
    } else {
      if (this.suppressedResources.some((arn) => resourceArn === arn)) {
        // Currently suppressed.  Remove the suppression.
        this.suppressedResources = this.suppressedResources.filter((arn) => resourceArn !== arn)
        if (resource.suppressedWeakDependeeOf.size === 0) {
          // This resource was suppressed, but it's not a weak dependee of anything.  This would happen
          // if we previously suppressed the resource when something else (that maintains a weak dependee)
          // was unselected.  It might also happen if things changed on the backend.
          // Regardless, the user wants to directly select this resource.
          this.selectedResources.push(resourceArn)
        }
      } else if (resource.weakDependeeOf.size > 0) {
        // Not currently suppressed -- suppress it
        this.suppressedResources.push(resourceArn)
      } else {
        this.selectedResources.push(resourceArn)
      }
    }

    // alert the containing form/model to the change in the value
    this.publishSelections()
  }

  getResourceTypePluralDisplayText(details: ResourceTypeDetails) {
    return details.pluralDisplayName
  }

  resourceSupportsRealTimeReplication(details: ResourceTypeDetails) {
    if (this.rpo === 0) {
      return details.supportsRealTime
    } else {
      return false
    }
  }

  setTab(tabName: string) {
    this.selectedTab = ResourceTypeClassification[tabName]
    this._sortedResourcesByType = null
  }

  isActiveTab(tabName: string) {
    return this.selectedTab === ResourceTypeClassification[tabName]
  }

  hasResourcesForTab(tabName: string) {
    const classification = ResourceTypeClassification[tabName]
    // A tab has content if it has at least one visible root resource for that classification.
    // Hidden containers (e.g. Azure Subscription: displayForSelection=false, no parentArn) and
    // suppressed child types are present in sourceResources but never rendered — ignore them so we
    // don't show empty tabs.
    return this.sourceResources.some((res) => {
      return !!res.resourceTypeDetails && res.resourceTypeDetails.classification === classification && isRenderable(res)
    })
  }

  /**
   * If the currently selected tab has no resources, auto-select the first tab that does.
   * This handles apps that may have no resources that trigger the default tab.
   */
  private autoSelectFirstAvailableTab() {
    if (this.hasResourcesForTab(ResourceTypeClassification[this.selectedTab])) {
      return
    }
    const tabOrder: ResourceTypeClassification[] = [
      ResourceTypeClassification.network,
      ResourceTypeClassification.compute,
      ResourceTypeClassification.storage,
      ResourceTypeClassification.k8s,
      ResourceTypeClassification.ai,
      ResourceTypeClassification.other,
    ]
    for (const tab of tabOrder) {
      if (this.hasResourcesForTab(ResourceTypeClassification[tab])) {
        this.selectedTab = tab
        return
      }
    }
  }
}
